import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Full Terms of Service page. Source of truth is
 * docs/legal/terms-of-service.md; this file mirrors that content
 * as JSX so the page inherits the app's typography and theming.
 * When updating, update BOTH files together.
 */
export default function TermsPage() {
  return (
    <div className="min-h-screen bg-canvas text-ink">
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" /> Back to the app
        </Link>

        <h1 className="font-display text-4xl font-semibold">Terms of Service</h1>
        <p className="mt-2 text-sm text-ink-muted">
          <strong>Visual Open Curriculum</strong>
          <br />
          Effective Date: [TODO: publication date] &middot; Last Updated: [TODO: publication date]
        </p>

        <div className="prose prose-slate mt-8 max-w-none space-y-4 text-[15px] leading-7 text-ink">
          <p>
            Welcome to Visual Open Curriculum (&ldquo;<strong>VOC</strong>,&rdquo; &ldquo;<strong>Service</strong>,&rdquo;{" "}
            &ldquo;<strong>we</strong>,&rdquo; &ldquo;<strong>us</strong>,&rdquo; or &ldquo;<strong>our</strong>&rdquo;).
            These Terms of Service (&ldquo;<strong>Terms</strong>&rdquo;) govern your access to and use of the VOC application,
            website, and related features (collectively, the &ldquo;<strong>Service</strong>&rdquo;). Please read these Terms
            carefully. By creating an account or otherwise using the Service, you agree to be bound by these Terms and by our
            Privacy Policy (coming soon).
          </p>
          <p>If you do not agree to these Terms, do not use the Service.</p>

          <Section num="1" title="About Us">
            <p>
              VOC is a free, non-commercial educational tool developed and operated on a volunteer basis by a group of alumni of
              Amherst College (the &ldquo;<strong>Team</strong>&rdquo;). We are not affiliated with, endorsed by, or acting as an
              agent of Amherst College, the University of Pennsylvania, or any other academic institution, unless expressly stated.
              We do not charge students or institutions to use the Service.
            </p>
            <p>
              For questions about these Terms, contact us at <em>[TODO: contact email, e.g. hi@novus-ai.org]</em>. For notices of a
              legal nature, write to us at <em>[TODO: mailing address for the responsible individual or legal entity]</em>.
            </p>
          </Section>

          <Section num="2" title="Definitions">
            <ul className="list-disc space-y-2 pl-6">
              <li><strong>&ldquo;Account&rdquo;</strong> means a registered user profile you create in order to access the Service.</li>
              <li><strong>&ldquo;Content&rdquo;</strong> means any text, notes, questions, feedback, course selections, and other information that you or other users submit to, upload to, or generate through the Service.</li>
              <li><strong>&ldquo;Institution&rdquo;</strong> means an educational institution whose course catalog is served through the Service.</li>
              <li><strong>&ldquo;Model Output&rdquo;</strong> means any recommendation, similar-course suggestion, natural-language answer, or other content that the Service produces using artificial intelligence or machine learning systems.</li>
              <li><strong>&ldquo;User&rdquo;</strong> or <strong>&ldquo;you&rdquo;</strong> means the individual accessing or using the Service.</li>
            </ul>
          </Section>

          <Section num="3" title="Eligibility">
            <p>
              The Service is intended for use by students, staff, and faculty of participating Institutions who are 18 years of age
              or older. If you are between 13 and 17 years old, you may use the Service only with the involvement and consent of a
              parent or legal guardian, and you represent that such consent has been obtained. Users under 13 are not permitted to
              use the Service.
            </p>
            <p>
              You represent and warrant that (a) you meet these age requirements, (b) you have full legal capacity to accept these
              Terms, and (c) your use of the Service does not violate any law or regulation applicable to you.
            </p>
          </Section>

          <Section num="4" title="Accounts">
            <p>To use most features of the Service, you must create an Account. When you create an Account, you agree to:</p>
            <ul className="list-disc space-y-1.5 pl-6">
              <li>Provide accurate, current, and complete information (including a valid institutional email address such as <code>@amherst.edu</code> where required).</li>
              <li>Keep your Account information up to date.</li>
              <li>Maintain the security of your password and take responsibility for any activity under your Account.</li>
              <li>Not share your Account with anyone else and not create multiple Accounts.</li>
              <li>Notify us promptly if you suspect any unauthorized use of your Account.</li>
            </ul>
            <p>
              We reserve the right to suspend, disable, or reclaim any Account that we reasonably determine violates these Terms or
              is used in a way that harms the Service or other Users.
            </p>
          </Section>

          <Section num="5" title="Use of the Service">
            <p>
              Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, non-sublicensable,
              revocable right to access and use the Service for your personal, non-commercial academic planning purposes.
            </p>
            <p>The Service is provided as an aid for course exploration and academic planning. It is <strong>not</strong>:</p>
            <ul className="list-disc space-y-1.5 pl-6">
              <li>An authoritative source of course availability, prerequisites, or graduation requirements;</li>
              <li>Academic advising or a substitute for consultation with a qualified academic advisor;</li>
              <li>A guarantee that any recommended course will be offered, that you will be permitted to enroll, or that any course sequence will satisfy degree requirements.</li>
            </ul>
            <p className="rounded-md bg-surface-alt p-4 text-[14px]">
              <strong>Always verify important information</strong> &mdash; including prerequisites, enrollment eligibility, and degree
              progress &mdash; with your institution&rsquo;s registrar, your academic advisor, or your institution&rsquo;s official
              course catalog before making enrollment or academic decisions.
            </p>
          </Section>

          <Section num="6" title="Acceptable Use">
            <p>You agree that you will not, and will not permit or encourage any other person to:</p>
            <ul className="list-disc space-y-1.5 pl-6">
              <li>Use the Service in any way that violates any applicable law, regulation, or the policies of any Institution;</li>
              <li>Impersonate any person or entity or falsely state or misrepresent your affiliation with an Institution;</li>
              <li>Use the Service to harass, threaten, intimidate, defame, or harm any other person;</li>
              <li>Post, submit, generate, or transmit content that is unlawful, harmful, abusive, defamatory, obscene, or that promotes violence, self-harm, or discrimination;</li>
              <li>Attempt to bypass, disable, or otherwise interfere with any content-moderation, filtering, security, or authentication mechanisms of the Service;</li>
              <li>Use the Service or Model Outputs to develop, train, or improve any competing product or artificial intelligence system, or to extract or reverse-engineer any underlying models or datasets;</li>
              <li>Scrape, harvest, or otherwise systematically collect Content from the Service, except as expressly permitted;</li>
              <li>Use the Service to send spam, unsolicited communications, or automated queries;</li>
              <li>Introduce viruses, malware, or other harmful code, or otherwise interfere with the Service&rsquo;s operation;</li>
              <li>Access the Service by any means other than the interfaces we provide;</li>
              <li>Use the Service in any way that infringes the intellectual property or other rights of any person;</li>
              <li>Use the Service to make legal, medical, financial, or other high-stakes decisions without independent professional verification.</li>
            </ul>
            <p>
              We reserve the right &mdash; but do not undertake the obligation &mdash; to review, restrict, or remove Content that we
              reasonably believe violates these Terms, and to suspend or terminate Accounts of Users who repeatedly or egregiously
              do so.
            </p>
          </Section>

          <Section num="7" title="Content You Provide">
            <p>
              You retain ownership of any Content you provide to the Service, including notes, feedback, questions, and
              course-history entries.
            </p>
            <p>
              By providing Content to the Service, you grant us a worldwide, non-exclusive, royalty-free, sublicensable, and
              transferable license to host, store, reproduce, process, transmit, display, and otherwise use that Content for the
              following purposes:
            </p>
            <ul className="list-disc space-y-1.5 pl-6">
              <li>Operating, providing, and improving the Service;</li>
              <li>Generating personalized recommendations and search results for you;</li>
              <li>Ensuring security and enforcing these Terms;</li>
              <li>Complying with legal obligations;</li>
              <li>If you have separately consented, contributing to academic research studies under <em>[TODO: IRB name/protocol]</em> (see Section 10 below).</li>
            </ul>
            <p>
              This license terminates when you delete your Content or your Account, except (a) as needed to retain backups and logs
              for a reasonable period, (b) as required by law, and (c) as applied to Content that has already been aggregated or
              de-identified for research purposes prior to deletion.
            </p>
            <p>
              You represent and warrant that (a) you have all necessary rights to any Content you submit, and (b) your Content does
              not violate these Terms or the rights of any third party.
            </p>
          </Section>

          <Section num="8" title="Artificial Intelligence Features">
            <p>
              The Service uses artificial intelligence and machine-learning systems provided by third parties (currently including
              Microsoft Azure OpenAI Service) to generate embeddings of course descriptions, to power natural-language and similarity
              search, and to produce Model Outputs such as course recommendations and explanations.
            </p>
            <p>You acknowledge and agree that:</p>
            <ul className="list-disc space-y-1.5 pl-6">
              <li><strong>Model Outputs may be inaccurate, incomplete, or inconsistent.</strong> They are generated automatically based on patterns in text and do not reflect the judgment of any human advisor.</li>
              <li><strong>Model Outputs are not authoritative.</strong> You should not rely on any Model Output as a substitute for consulting an official course catalog, registrar, or academic advisor.</li>
              <li><strong>You are responsible for how you use Model Outputs.</strong> Do not use them as the sole basis for enrollment decisions, degree planning, or any other consequential decision.</li>
              <li><strong>We cannot guarantee availability of AI features.</strong> Model providers may modify, throttle, or suspend their services at any time, and content filters imposed by those providers may cause certain requests to fail or be refused.</li>
              <li><strong>Free-text inputs to AI features are processed by third parties.</strong> Text you enter into search boxes, Surprise Me notes, and similar inputs may be transmitted to our AI service providers for processing. Do not submit sensitive personal, medical, financial, or legally protected information into these fields.</li>
              <li><strong>Content moderation.</strong> The Service and our AI providers may apply automated content-moderation filters. We reserve the right to prevent certain Content &mdash; including violent, harmful, or self-injurious content &mdash; from being submitted to our AI systems or used to generate Model Outputs. If you or someone you know is experiencing a mental-health crisis, please contact <em>[TODO: e.g. Amherst Counseling Center at 413-542-2354]</em> or the 988 Suicide and Crisis Lifeline.</li>
            </ul>
          </Section>

          <Section num="9" title="Third-Party Services">
            <p>The Service integrates with, or transmits data to, several third-party services in order to function. These currently include, without limitation:</p>
            <ul className="list-disc space-y-1.5 pl-6">
              <li><strong>Supabase</strong> (authentication and database)</li>
              <li><strong>Microsoft Azure OpenAI Service</strong> (embeddings and chat models)</li>
              <li><strong>Qdrant</strong> (vector search)</li>
              <li><strong>Vercel</strong> (frontend hosting)</li>
              <li><strong>Render</strong> (backend hosting)</li>
              <li><strong>PostHog</strong> (product analytics)</li>
            </ul>
            <p>
              Each of these providers processes data in accordance with its own terms and privacy policies. Your use of the Service
              is also subject to their terms to the extent applicable. We are not responsible for the acts, omissions, or policies of
              third-party providers. We may add, replace, or discontinue third-party providers at any time and will update our
              Privacy Policy accordingly.
            </p>
          </Section>

          <Section num="10" title="Privacy and Research">
            <p>
              Our collection, use, and disclosure of your personal information is described in our Privacy Policy (coming soon),
              which is incorporated into these Terms by reference. Please review it carefully.
            </p>
            <p>
              Separately, you may &mdash; but are not required to &mdash; consent to your anonymized data being used in academic
              research studies conducted under approved institutional review board oversight. Product use, features, and eligibility
              do not depend on whether you provide this research consent.
            </p>
          </Section>

          <Section num="11" title="Modifications to the Service">
            <p>We are a small volunteer team and the Service is under active development. We may, at any time and without notice:</p>
            <ul className="list-disc space-y-1.5 pl-6">
              <li>Add, modify, or remove features;</li>
              <li>Change the AI models or third-party services used by the Service;</li>
              <li>Change the Institutions whose catalogs are supported;</li>
              <li>Suspend or discontinue the Service in whole or in part;</li>
              <li>Impose limits on usage, storage, or requests to prevent abuse or manage costs.</li>
            </ul>
            <p>We will make reasonable efforts to notify Users of material changes but cannot guarantee advance notice in all cases.</p>
          </Section>

          <Section num="12" title="Termination">
            <p>
              You may stop using the Service and delete your Account at any time through the account settings in the Service, or by
              contacting us at <em>[TODO: contact email]</em>. Deletion is described further in our Privacy Policy.
            </p>
            <p>
              We may suspend or terminate your Account or your access to the Service at any time, with or without notice, if we
              reasonably believe that (a) you have violated these Terms, (b) your use presents a risk to the Service or its Users,
              (c) providing the Service to you is no longer commercially or operationally sustainable, or (d) we are required to do
              so by law.
            </p>
            <p>Sections 5&ndash;8, 12&ndash;17, and 19 shall survive any termination of these Terms.</p>
          </Section>

          <Section num="13" title="Disclaimer of Warranties">
            <p className="font-medium">
              THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE,&rdquo; WITHOUT WARRANTIES OF ANY KIND, EITHER
              EXPRESS OR IMPLIED.
            </p>
            <p>
              To the fullest extent permitted by applicable law, we disclaim all warranties, express or implied, including without
              limitation any warranties of merchantability, fitness for a particular purpose, non-infringement, quiet enjoyment,
              accuracy, or availability. We do not warrant that the Service will be uninterrupted, error-free, or free of harmful
              components, or that any Content or Model Output will be accurate, reliable, or suitable for your purposes.
            </p>
            <p>
              Some jurisdictions do not allow the exclusion of certain warranties. In such jurisdictions, the above exclusions apply
              only to the extent permitted by law.
            </p>
          </Section>

          <Section num="14" title="Limitation of Liability">
            <p className="font-medium">
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE TEAM, ITS INDIVIDUAL MEMBERS, VOLUNTEERS, OR
              AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING
              BUT NOT LIMITED TO LOSS OF PROFITS, DATA, GOODWILL, OR ACADEMIC OPPORTUNITIES, ARISING OUT OF OR RELATED TO YOUR USE
              OF OR INABILITY TO USE THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>
            <p className="font-medium">
              OUR AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED
              FIFTY U.S. DOLLARS ($50) OR THE AMOUNT YOU HAVE PAID US IN THE PAST TWELVE MONTHS (WHICH IS ORDINARILY ZERO),
              WHICHEVER IS GREATER.
            </p>
            <p>
              Because the Service is provided without charge, you agree that this limitation is reasonable. Some jurisdictions do
              not allow the exclusion or limitation of certain damages. In such jurisdictions, the above limitations apply only to
              the extent permitted by law.
            </p>
          </Section>

          <Section num="15" title="Indemnification">
            <p>
              To the fullest extent permitted by applicable law, you agree to defend, indemnify, and hold harmless the Team, its
              individual members, volunteers, and affiliates from and against any and all claims, damages, obligations, losses,
              liabilities, costs, and expenses (including reasonable attorneys&rsquo; fees) arising from or related to: (a) your use
              of the Service; (b) your Content; (c) your violation of these Terms; (d) your violation of any right of a third party,
              including intellectual-property, publicity, or privacy rights; or (e) your violation of any law or regulation.
            </p>
          </Section>

          <Section num="16" title="Governing Law and Dispute Resolution">
            <p>
              These Terms and any dispute arising out of or related to these Terms or the Service shall be governed by the laws of
              the Commonwealth of Massachusetts, without regard to its conflict-of-law provisions. You agree that any action, suit,
              or proceeding shall be brought exclusively in the state or federal courts located in Hampshire County, Massachusetts,
              and you consent to the personal jurisdiction of those courts.
            </p>
            <p>
              Before initiating any formal proceeding, you agree to first contact us at <em>[TODO: contact email]</em> and attempt
              in good faith to resolve the dispute informally for at least thirty (30) days.
            </p>
          </Section>

          <Section num="17" title="Changes to These Terms">
            <p>
              We may update these Terms from time to time. If we make material changes, we will notify Users through the Service, by
              email, or by other reasonable means, and will update the &ldquo;Last Updated&rdquo; date at the top of these Terms.
              Your continued use of the Service after the updated Terms take effect constitutes your acceptance of the changes. If
              you do not agree with the updated Terms, you must stop using the Service and may delete your Account.
            </p>
          </Section>

          <Section num="18" title="General Provisions">
            <ul className="list-disc space-y-2 pl-6">
              <li><strong>Entire Agreement.</strong> These Terms, together with the Privacy Policy and Research Consent document, constitute the entire agreement between you and us regarding the Service.</li>
              <li><strong>Severability.</strong> If any provision of these Terms is found to be unenforceable, the remaining provisions shall remain in full force and effect.</li>
              <li><strong>No Waiver.</strong> Our failure to enforce any right or provision of these Terms shall not be deemed a waiver of that right or provision.</li>
              <li><strong>Assignment.</strong> You may not assign or transfer these Terms without our prior written consent. We may assign these Terms without your consent in connection with a merger, acquisition, sale of assets, or by operation of law.</li>
              <li><strong>No Agency.</strong> Nothing in these Terms creates any agency, partnership, joint venture, employment, or franchise relationship between you and us.</li>
              <li><strong>Force Majeure.</strong> We shall not be liable for any failure or delay in performance caused by circumstances beyond our reasonable control, including natural disasters, government actions, network outages, or actions of third-party service providers.</li>
              <li><strong>Notices.</strong> Notices to you may be sent to the email address associated with your Account. Notices to us must be sent to <em>[TODO: contact email]</em>.</li>
            </ul>
          </Section>

          <Section num="19" title="Contact">
            <p>If you have any questions about these Terms, please contact us at:</p>
            <p className="rounded-md bg-surface-alt p-4 text-[14px] leading-6">
              <strong>Visual Open Curriculum</strong>
              <br />
              Email: <em>[TODO: e.g. hi@novus-ai.org]</em>
              <br />
              Address: <em>[TODO: mailing address or &ldquo;c/o [name], [address]&rdquo;]</em>
            </p>
          </Section>

          <p className="mt-8 text-sm italic text-ink-muted">
            These Terms were last updated on [TODO: date].
          </p>
        </div>
      </div>
    </div>
  );
}

function Section({ num, title, children }) {
  return (
    <section className="pt-4">
      <h2 className="font-display text-xl font-semibold text-ink">
        {num}. {title}
      </h2>
      <div className="mt-2 space-y-3">{children}</div>
    </section>
  );
}
